import React, { useEffect, useState } from 'react';
import { CONTACT_EMAIL } from '../../contact/config';
import DocumentPage from '../../components/documentPage/documentPage';
import LegalDocumentBody from '../../components/legalDocument/legalDocument';
import SandwichContainer from '../../components/sandwichContainer/sandwichContainer';
import { loadLegalDocuments } from '../../legal/client';
import type { LegalDocument } from '../../legal/interfaces';
import { pad2 } from '../../utils';
import { styles } from './styles';

// playercafe.novaeon.io/legal — the published Terms of Use and Privacy Policy.
//
// The app links here and NEVER bundles a copy: one canonical document cannot drift from what
// App Review reads, and a correction ships without an App Store submission. Design record:
// plans/PLAYERCAFE_LEGAL_PAGE_PLAN.md.
//
// Both documents live on ONE page with `#privacy` and `#terms` anchors. App Store Connect's
// Privacy Policy URL field must point at the anchored form, or a reviewer lands on whichever
// document happens to be first.
//
// WHICH Firestore document is chosen by hostname in index.tsx, not here — one Hosting site
// serves every attached domain, so the host is the only thing distinguishing
// playercafe.novaeon.io from novaeon.io.
const COLLECTION = 'products';

// Field name → the row that displays it, in order. The anchor is what the app and App Store
// Connect link to, so these strings are part of the public contract.
const DOCUMENTS = [
  { field: 'privacyPolicy', anchor: 'privacy', title: 'Privacy Policy' },
  { field: 'termsOfUse', anchor: 'terms', title: 'Terms of Use' },
] as const;

// Generous cap for the expanded panel — these documents run to a few thousand pixels and the
// SandwichContainer default (500px) would guillotine them. max-height has to be a real length
// because it is what the open/close transition animates.
const PANEL_MAX_HEIGHT = '20000px';

interface LegalProps {
  /** Firestore document id under `products/` — chosen by hostname in index.tsx. */
  productId: string;
}

const Legal: React.FC<LegalProps> = ({ productId }) => {

  const [documents, setDocuments] = useState<LegalDocument[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {

    let cancelled = false;

    (async () => {
      try {
        const loaded = await loadLegalDocuments(COLLECTION, productId, DOCUMENTS.map(d => d.field));

        if (!cancelled) { setDocuments(loaded); }
      }
      catch (error) {
        // Logged, not surfaced: a visitor cannot act on the difference between "rules not
        // deployed" and "malformed JSON in the console", but whoever is debugging can.
        console.error(error);

        if (!cancelled) { setFailed(true); }
      }
    })();

    return () => { cancelled = true; };
  }, [productId]);

  // The anchor a visitor arrived on. Captured once at mount rather than read inside the
  // effect below, because the rows use it as their INITIAL open state — a value that arrives
  // after they mount would never reach useState.
  const [anchor] = useState(() => window.location.hash.slice(1));

  // Scrolling has to wait for the text: the browser resolves `#privacy` before the fetch
  // does, finds nothing, and leaves you at the top of the page.
  useEffect(() => {

    if (!documents || !DOCUMENTS.some(entry => entry.anchor === anchor)) { return; }

    window.document.getElementById(anchor)?.scrollIntoView();
  }, [documents, anchor]);

  return (
    <DocumentPage>
      <h1 style={styles.pageTitle}>Legal</h1>

      {failed ? (
        <div style={styles.fallback}>
          <p style={styles.paragraph}>
            We could not load these documents right now. This is a problem on our side, not
            yours. Please try again shortly, or email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={styles.link}>{CONTACT_EMAIL}</a> and we
            will send you a copy of the Terms of Use and Privacy Policy directly.
          </p>
        </div>
      ) : !documents ? (
        <p style={styles.status}>Loading…</p>
      ) : (
        DOCUMENTS.map((entry, index) => (
          <section key={entry.field} id={entry.anchor}>
            <SandwichContainer
              index={pad2(index)}
              title={entry.title}
              maxHeight={PANEL_MAX_HEIGHT}
              startOpen={anchor === entry.anchor}
              isLast={index === DOCUMENTS.length - 1}
            >
              <LegalDocumentBody document={documents[index]} />
            </SandwichContainer>
          </section>
        ))
      )}
    </DocumentPage>
  );
};

export default Legal;
